/**
 * ContractReview — upload the executed contract and run the agent
 * review agent, then render its grounded, cited compliance opinion.
 *
 * This replaces the self-attested "tick 14 boxes → 100%" model: the agent reads
 * the actual contract, classifies it, and reports each required provision as
 * met / deficient / missing with a verbatim evidence quote, citation, analysis,
 * and a redline. It is decision support; a human signs off.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import SlaRegister from './SlaRegister';

const API = process.env.REACT_APP_API_URL;

const RUNNING = new Set(['queued', 'classifying', 'reviewing', 'verifying']);

// Findings speak as suggestions to the reviewer, never as verdicts on the
// plan (operator rule): the row states what was verified or what the agent
// suggests, and expands on click for the evidence.
const STATUS_CFG = {
  present_adequate:     { label: 'Verified',              dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  present_deficient:    { label: 'Suggest strengthening', dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200' },
  missing:              { label: 'Suggest adding',        dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50 border-red-200' },
  not_applicable:       { label: 'Not applicable',        dot: 'bg-slate-300',   text: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200' },
  unable_to_determine:  { label: 'Could not verify',      dot: 'bg-slate-400',   text: 'text-slate-600',   bg: 'bg-slate-50 border-slate-200' },
};
// Priorities, not severities: the chip tells the reviewer where to look
// first, it does not characterize the plan's exposure.
const SEV_CFG = {
  critical: { label: 'P1', cls: 'text-red-700 bg-red-100' },
  high:     { label: 'P2', cls: 'text-orange-700 bg-orange-100' },
  medium:   { label: 'P3', cls: 'text-amber-700 bg-amber-100' },
  low:      { label: 'P4', cls: 'text-slate-600 bg-slate-100' },
};
const POSTURE_CFG = {
  compliant:          { label: 'Strong alignment',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  minor_deficiencies: { label: 'A few suggestions',     color: 'bg-amber-50 text-amber-700 border-amber-200' },
  material_gaps:      { label: 'Several suggestions',   color: 'bg-amber-50 text-amber-800 border-amber-300' },
  not_assessable:     { label: 'More documents needed', color: 'bg-slate-50 text-slate-600 border-slate-200' },
};
const TYPE_LABEL = {
  claims_tpa: 'Claims / TPA', utilization_management: 'Utilization Mgmt', pbm: 'PBM',
  credentialing: 'Credentialing', marketing_sales: 'Marketing / Sales', enrollment: 'Enrollment',
  customer_service: 'Customer Service', provider_primary_care: 'Provider — Primary Care',
  provider_specialty: 'Provider — Specialty', hospital_facility: 'Hospital / Facility',
  baa: 'Business Associate Agreement', other: 'Other / Unclassified',
};
const PROGRAM_LABEL = {
  medicare_advantage: 'Medicare Advantage', part_d: 'Part D', medicaid: 'Medicaid',
  dual_dsnp: 'Dual / D-SNP', unknown: 'Program not identified',
};
const DEVIATION_CFG = {
  unchanged:          { label: 'Unchanged',   cls: 'text-slate-500 bg-slate-100' },
  reworded_equivalent:{ label: 'Reworded (equivalent)', cls: 'text-sky-700 bg-sky-100' },
  weakened:           { label: 'Suggest restoring (narrowed)',   cls: 'text-amber-700 bg-amber-100' },
  removed:            { label: 'Suggest restoring (not carried)', cls: 'text-amber-800 bg-amber-100' },
  added:              { label: 'Added',       cls: 'text-indigo-700 bg-indigo-100' },
  absent_from_both:   { label: 'Suggest adding to template too', cls: 'text-amber-700 bg-amber-100' },
};
const TYPE_OPTS = [
  ['claims_tpa','Claims / TPA'],['utilization_management','Utilization Mgmt'],['pbm','PBM'],
  ['credentialing','Credentialing'],['marketing_sales','Marketing / Sales'],['enrollment','Enrollment'],
  ['customer_service','Customer Service'],['provider_primary_care','Provider — Primary Care'],
  ['provider_specialty','Provider — Specialty'],['hospital_facility','Hospital / Facility'],
  ['baa','Business Associate Agreement'],['other','Other'],
];
const PROGRAM_OPTS = [['medicare_advantage','Medicare Advantage'],['part_d','Part D'],['medicaid','Medicaid'],['dual_dsnp','Dual / D-SNP']];

function Finding({ f }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CFG[f.status] || STATUS_CFG.unable_to_determine;
  const dev = f.deviation_type ? (DEVIATION_CFG[f.deviation_type] || null) : null;
  return (
    <div className={`rounded-xl border ${cfg.bg} overflow-hidden`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 px-4 py-3 text-left">
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{f.provision_title}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${(SEV_CFG[f.severity] || SEV_CFG.low).cls}`}>{(SEV_CFG[f.severity] || SEV_CFG.low).label}</span>
            {dev
              ? <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${dev.cls}`}>{dev.label}</span>
              : <span className={`text-[11px] font-semibold ${cfg.text}`}>{cfg.label}</span>}
            {f.verified && <span className="text-[10px] text-emerald-600" title="Confirmed by the verification pass">✓ verified</span>}
            {f.recovered && <span className="text-[10px] text-indigo-600" title="Located by a targeted second search of the full contract after the first pass missed it">⤴ recovered on second search</span>}
          </div>
          <div className="text-[11px] font-mono text-slate-400 mt-0.5">{f.requirement_citation}</div>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-9 space-y-2 text-sm">
          {f.analysis && <p className="text-slate-700">{f.analysis}</p>}
          {f.authority_text && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Controlling authority</div>
              <p className="text-[12px] text-slate-500">{f.authority_text}</p>
            </div>
          )}
          {f.template_quote && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Approved template language</div>
              <blockquote className="border-l-2 border-emerald-300 pl-3 italic text-slate-600 text-[13px]">{f.template_quote}</blockquote>
            </div>
          )}
          {f.evidence_quote && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                {f.template_quote || dev ? `Executed contract language${f.evidence_location ? ` · ${f.evidence_location}` : ''}` : `Evidence (verbatim${f.source_document ? ` from ${f.source_document}` : ' from contract'}${f.evidence_location ? ` · ${f.evidence_location}` : ''})`}
              </div>
              <blockquote className={`border-l-2 pl-3 italic text-slate-600 text-[13px] ${dev ? 'border-amber-300' : 'border-slate-300'}`}>{f.evidence_quote}</blockquote>
            </div>
          )}
          {f.absence_rationale && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Why we make this suggestion</div>
              <p className="text-[12px] text-slate-500">{f.absence_rationale}</p>
            </div>
          )}
          {f.recommended_redline && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-orange-500 mb-1">Suggested language</div>
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-700">{f.recommended_redline}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateManager({ templates, reload }) {
  const [name, setName] = useState('');
  const [ctype, setCtype] = useState('');
  const [program, setProgram] = useState('');
  const [state, setState] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name || file.name);
      if (ctype) fd.append('contract_type', ctype);
      if (program) fd.append('program', program);
      if (state) fd.append('state', state);
      const res = await fetch(`${API}/api/fdr/contract-templates`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Upload failed');
      setName(''); setCtype(''); setProgram(''); setState('');
      await reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const retire = async (id) => {
    if (!window.confirm('Retire this template? It will no longer be selectable for conformance checks.')) return;
    try {
      const res = await fetch(`${API}/api/fdr/contract-templates/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.success) await reload();
    } catch {}
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Approved templates</div>
      {templates.length === 0
        ? <div className="text-xs text-slate-500">No templates yet. Upload your approved standard contract once below, then reuse it for conformance checks across every vendor.</div>
        : (
          <ul className="space-y-1">
            {templates.map(t => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-700 truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-400">
                    {[TYPE_LABEL[t.contract_type] || t.contract_type, PROGRAM_LABEL[t.program] || t.program, t.state].filter(Boolean).join(' · ') || 'untagged'}
                    {t.extraction_status && t.extraction_status !== 'completed' && <span className="ml-1 text-amber-600">({t.extraction_status})</span>}
                  </div>
                </div>
                <button onClick={() => retire(t.id)} className="text-slate-400 hover:text-red-600 text-[11px] flex-shrink-0">Retire</button>
              </li>
            ))}
          </ul>
        )}
      <div className="grid grid-cols-2 gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Template name (e.g., Standard Provider Agreement 2026)" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 col-span-2" />
        <select value={ctype} onChange={e => setCtype(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
          <option value="">Contract type…</option>
          {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={program} onChange={e => setProgram(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
          <option value="">Program…</option>
          {PROGRAM_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={state} onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="State (e.g., FL)" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {busy ? 'Uploading…' : '↑ Upload template'}
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

// documentsManaged: the Documents tab owns uploads and the viewer; this
// component then shows only the review controls and the findings.
export default function ContractReview({ contractId, documentsManaged = false }) {
  const [review, setReview] = useState(null);
  const [findings, setFindings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [viewDocId, setViewDocId] = useState(null);
  const [showBriefing, setShowBriefing] = useState(false);
  const [showVerified, setShowVerified] = useState(false);
  const [showSlas, setShowSlas] = useState(false);
  const [uploadRole, setUploadRole] = useState('contract');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('full');       // full | amendment | conformance
  const [baseDocId, setBaseDocId] = useState('');  // base contract for amendment mode
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(''); // approved template for conformance mode
  const [showTemplates, setShowTemplates] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/review`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) { setReview(data.review); setFindings(data.findings || []); setDocuments(data.documents || []); }
    } catch {}
    setLoading(false);
  }, [contractId]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fdr/contract-templates`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setTemplates(data.templates || []);
    } catch {}
  }, []);

  useEffect(() => { load(); loadTemplates(); }, [load, loadTemplates]);

  // Poll while a review is in flight.
  useEffect(() => {
    if (!review || !RUNNING.has(review.status)) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [review, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_role', uploadRole);
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/document`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Upload failed');
      await load();
    } catch (e) { setError(e.message); }
    setUploading(false);
  };

  const runReview = async () => {
    setRunning(true); setError('');
    try {
      const body = mode === 'amendment'
        ? { review_mode: 'amendment', document_id: documents[0]?.id, base_document_id: baseDocId }
        : mode === 'conformance'
        ? { review_mode: 'conformance', document_id: documents[0]?.id, template_id: templateId }
        : { review_mode: 'full' };
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/review`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not start review');
      await load();
    } catch (e) { setError(e.message); }
    setRunning(false);
  };

  const isRunning = review && RUNNING.has(review.status);
  const posture = review?.compliance_posture ? (POSTURE_CFG[review.compliance_posture] || POSTURE_CFG.not_assessable) : null;
  const actions = review?.token_usage?.top_actions || [];

  if (loading) return <div className="text-sm text-slate-400 py-4">Loading review…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          {documentsManaged ? 'Compliance review' : 'Contract Review'}
        </h4>
        <div className="flex items-center gap-2">
          {!documentsManaged && (
            <>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
              <select value={uploadRole} onChange={e => setUploadRole(e.target.value)}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" title="What the next uploaded file is">
                <option value="contract">Contract</option>
                <option value="baa">Business Associate Agreement</option>
                <option value="amendment">Amendment</option>
              </select>
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {uploading ? 'Uploading…' : `↑ Upload ${uploadRole === 'baa' ? 'BAA' : uploadRole}`}
              </button>
            </>
          )}
          <select value={mode} onChange={e => setMode(e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" title="What kind of review to run">
            <option value="full">Full review (off-template / new)</option>
            <option value="conformance">Conformance vs our template</option>
            {documents.length > 1 && <option value="amendment">Amendment vs base</option>}
          </select>
          {mode === 'conformance' && (
            <select value={templateId} onChange={e => setTemplateId(e.target.value)}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" title="Approved template to compare against">
              <option value="">Approved template…</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {mode === 'amendment' && (
            <select value={baseDocId} onChange={e => setBaseDocId(e.target.value)}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" title="Base contract the newest upload amends">
              <option value="">Base contract…</option>
              {documents.slice(1).map(d => <option key={d.id} value={d.id}>{d.file_name}</option>)}
            </select>
          )}
          <button onClick={() => setShowTemplates(s => !s)}
            className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50" title="Manage approved templates">
            Templates
          </button>
          <button onClick={runReview} disabled={running || isRunning || !documents.length || (mode === 'amendment' && !baseDocId) || (mode === 'conformance' && !templateId)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50"
            title={!documents.length ? 'Upload the contract first' : (mode === 'conformance' && !templateId ? 'Pick an approved template' : 'Run the review')}>
            {isRunning ? 'Reviewing…' : (running ? 'Starting…' : (mode === 'amendment' ? 'Run amendment review' : mode === 'conformance' ? 'Run conformance check' : 'Run full review'))}
          </button>
        </div>
      </div>

      {showTemplates && <TemplateManager templates={templates} reload={loadTemplates} />}

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {/* SCOPE — always stated, never implied: exactly which documents the
          next run will read, and which ones the shown report read. */}
      {documents.length > 0 && (() => {
        const hasContract = documents.some(d => (d.doc_role || 'contract') === 'contract');
        const hasBaa = documents.some(d => d.doc_role === 'baa');
        const nextScope = hasContract && hasBaa
          ? 'The next run reads the contract and the BAA together. PHI provisions are assessed across both.'
          : hasBaa
            ? 'Only a BAA is on file. The next run reads the BAA alone; delegation provisions outside it cannot be assessed until the services agreement is uploaded.'
            : 'Only the contract is on file. The next run reads the contract alone; if a BAA exists, upload it first so privacy findings are not raised against gaps the BAA already covers.';
        return (
          <div className={`text-xs rounded-xl px-3.5 py-2.5 border font-medium ${hasContract && hasBaa
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            {nextScope}
          </div>
        );
      })()}
      {review?.scope_note && (
        <div className="text-xs rounded-xl px-3.5 py-2.5 border bg-sky-50 border-sky-200 text-sky-900 font-medium">
          What this report covered: {review.scope_note}
        </div>
      )}
      {!documentsManaged && documents.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span>Documents:</span>
          {documents.map((d) => (
            <button key={d.id} type="button" onClick={() => setViewDocId(viewDocId === d.id ? null : d.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border font-medium ${
                viewDocId === d.id ? 'border-orange-400 bg-orange-50 text-orange-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
              <span aria-hidden>📄</span>
              <span className="max-w-[240px] truncate">{d.file_name}</span>
              {d.doc_role === 'amendment' && <span className="text-[9.5px] font-bold uppercase text-sky-600">amendment</span>}
              {d.page_count ? <span className="text-slate-400">{d.page_count} pp</span> : null}
              {d.extraction_status && d.extraction_status !== 'completed' && <span className="text-amber-600">({d.extraction_status})</span>}
            </button>
          ))}
        </div>
      )}
      {!documentsManaged && viewDocId && (() => {
        const d = documents.find((x) => x.id === viewDocId);
        if (!d) return null;
        const isPdf = /pdf/i.test(d.mime_type || d.file_name || '');
        const url = `${API}/api/fdr/contracts/${contractId}/documents/${d.id}/file`;
        return (
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-200">
              <span className="text-xs font-semibold text-slate-700 truncate">{d.file_name}</span>
              <div className="flex items-center gap-2.5">
                <a href={isPdf ? `${url}?inline=1` : url} target="_blank" rel="noreferrer"
                  className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">Open in new tab ↗</a>
                <button type="button" onClick={() => setViewDocId(null)}
                  className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800">Close</button>
              </div>
            </div>
            {isPdf ? (
              <iframe title={d.file_name} src={`${url}?inline=1`} className="w-full bg-white" style={{ height: '72vh', border: 0 }} />
            ) : (
              <div className="px-4 py-6 text-center text-xs text-slate-500">
                This file type does not preview in the browser. <a className="font-semibold text-orange-700" href={url}>Download {d.file_name}</a> to read it.
              </div>
            )}
          </div>
        );
      })()}

      {!documents.length && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
          {documentsManaged
            ? 'Upload the executed contract under Documents. The review then reads it clause by clause against the CMS and, where applicable, state Medicaid requirements.'
            : 'Upload the executed contract (PDF or Word). The agent will classify it, then review it clause-by-clause against the CMS and, where applicable, state Medicaid requirements.'}
        </div>
      )}

      {isRunning && (
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
            <span>{review.progress_msg || 'Reviewing…'}</span>
            <span>{review.progress_pct || 0}%</span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-500 to-orange-500 rounded-full transition-all" style={{ width: `${review.progress_pct || 0}%` }} />
          </div>
        </div>
      )}

      {review && review.status === 'failed' && (
        <div className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">Review failed: {review.error_message || 'unknown error'}. You can re-run it.</div>
      )}

      {review && review.status === 'completed' && (
        <div className="space-y-4">
          {/* Classification + posture */}
          <div className="flex items-start justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-slate-800">{TYPE_LABEL[review.detected_contract_type] || review.detected_contract_type}</span>
                <span className="text-[11px] text-slate-500">· {PROGRAM_LABEL[review.detected_program] || review.detected_program}{review.detected_state ? ` (${review.detected_state})` : ''}</span>
                {review.review_mode === 'conformance' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Conformance vs {review.template_name || 'template'}</span>}
              </div>
              {review.classification_rationale && <p className="text-xs text-slate-500 mt-1 max-w-2xl">{review.classification_rationale}</p>}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* No percentage grade, by operator rule: the agent advises the
                  committee, it does not score the health plan. The posture
                  chip and the counts below are facts, not a grade. */}
              {posture && <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${posture.color}`}>{posture.label}</span>}
            </div>
          </div>

          {/* No tally strip: counts read like a scorecard. The section
              headers below carry the numbers where the work actually is. */}

          {/* Amendment change / conformance redline summary */}
          {review.amendment_summary && (review.review_mode === 'amendment' || review.review_mode === 'conformance') && (
            <div className="bg-orange-50/50 border border-orange-200 rounded-xl p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-orange-500 mb-1">
                {review.review_mode === 'conformance' ? `Vendor redlines vs ${review.template_name || 'your template'}` : 'What this amendment changes'}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{review.amendment_summary}</p>
            </div>
          )}

          {/* Opinion + actions — collapsed by default (operator rule): the
              findings are the substance; the narrative is there for whoever
              wants it, one click away. */}
          {review.overall_opinion && (
            <div className="bg-slate-50 rounded-xl overflow-hidden">
              <button type="button" onClick={() => setShowBriefing(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-100/60">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Executive briefing</span>
                <span className="text-[11px] font-semibold text-slate-500">{showBriefing ? 'Hide' : 'Show'}</span>
              </button>
              {showBriefing && (
              <div className="px-4 pb-4">
              <p className="text-sm text-slate-700 leading-relaxed">{review.overall_opinion}</p>
              {actions.length > 0 && (
                <ol className="list-decimal list-inside mt-3 space-y-1 text-sm text-slate-700">
                  {actions.map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              )}
              </div>
              )}
            </div>
          )}

          {/* SLAs & timeframes — collapsed by operator rule: one quiet bar,
              click to expand the register. Same page, no noise. */}
          <div className="bg-slate-50 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowSlas(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-100/60">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">SLAs &amp; timeframes</span>
              <span className="text-[11px] font-semibold text-slate-500">{showSlas ? 'Hide' : 'Show'}</span>
            </button>
            {showSlas && (
              <div className="px-4 pb-4">
                <SlaRegister contractId={contractId} embedded />
              </div>
            )}
          </div>

          {/* Suggestions first (the work), verified provisions collapsed
              below (the assurance) — triage order, not report order. */}
          {(() => {
            const suggestions = findings.filter(f => !['present_adequate', 'not_applicable'].includes(f.status));
            const verified = findings.filter(f => ['present_adequate', 'not_applicable'].includes(f.status));
            return (
              <>
                {suggestions.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Suggestions · {suggestions.length}</div>
                    {suggestions.map(f => <Finding key={f.id} f={f} />)}
                  </div>
                )}
                {verified.length > 0 && (
                  <div className="space-y-2">
                    <button type="button" onClick={() => setShowVerified(v => !v)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-emerald-50/60 border border-emerald-100 text-left hover:bg-emerald-50">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Verified provisions · {verified.length}</span>
                      <span className="text-[11px] font-semibold text-emerald-700">{showVerified ? 'Hide' : 'Show'}</span>
                    </button>
                    {showVerified && verified.map(f => <Finding key={f.id} f={f} />)}
                  </div>
                )}
              </>
            );
          })()}

          {/* Off-checklist obligations the contract imposes (advisory) */}
          {Array.isArray(review.additional_obligations) && review.additional_obligations.filter(o => !o.checklist_key).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Additional obligations in this contract ({review.additional_obligations.filter(o => !o.checklist_key).length}) · beyond the regulatory checklist, advisory
              </div>
              <ul className="space-y-2">
                {review.additional_obligations.filter(o => !o.checklist_key).map((o, i) => (
                  <li key={i} className="text-[13px] text-slate-700">
                    <div className="flex items-center gap-2">
                      {o.topic && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{o.topic}</span>}
                      {o.party && <span className="text-[10px] text-slate-400">· {o.party}</span>}
                    </div>
                    <div>{o.obligation}</div>
                    {o.quote && <blockquote className="border-l-2 border-slate-200 pl-2 italic text-slate-500 text-[12px] mt-0.5">{o.quote}</blockquote>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-slate-400 italic">
            This review is produced by an AI agent, and AI can make mistakes: a citation may be imprecise, a clause may be misread, or a provision may be missed. It is decision support, not legal advice, and it decides nothing. Verify the quoted language against the documents, and have a qualified attorney or your compliance officer review and sign off before acting on anything here.
          </p>
        </div>
      )}
    </div>
  );
}
